import { Centered, EmptyState, FlexColumn, FlexRow, MaterialIcon, useMoment } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LoadingSpinner } from 'baseui/button/styled-components';
import { LabelLarge } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { useStyles } from '../../../../hooks/useStyles';
import { getDeviceConnectionStatus } from '../../../../services/streamingService';
import { playbackStreamingCurrentStateState, playbackStreamingState } from '../../../../storage/streaming';
import { RightCard } from '../../../../types/app';
import { MinimalApiAsset } from '../../../../types/asset';
import { CaptureDuration } from '../../../../types/streaming';
import FloatingRightCard from '../../../layout/cards/FloatingRightCard';
import { StyledTooltip } from '../../../ui/StyledTooltip';
import PlaybackWindow from './PlaybackWindow';

type Props = Readonly<{
  duration: CaptureDuration;
}>;

function FloatingPlaybackWindow({ duration }: Props) {
  const { css, theme, classes } = useStyles();
  const [connected, setConnected] = useState<boolean>();
  const [reload, setReload] = useState(0);
  const playbackStreaming = useRecoilValue(playbackStreamingState);
  const resetPlaybackStreaming = useResetRecoilState(playbackStreamingState);
  const { formatDateFromTs } = useMoment();
  const currentState = useRecoilValue(playbackStreamingCurrentStateState);
  const asset: MinimalApiAsset | undefined =
    currentState?.asset?.subClient && currentState?.asset.client
      ? {
          assetId: currentState.asset._id,
          clientId: currentState.asset.client._id,
          subClientId: currentState.asset.subClient._id,
        }
      : undefined;

  useEffect(() => {
    setConnected(undefined);
    const validateConnection = async () => {
      if (!asset) {
        setConnected(false);
        return;
      }
      const { connected, error } = await getDeviceConnectionStatus({
        assetId: asset.assetId,
        clientId: asset.clientId,
        subClientId: asset.subClientId,
      });
      if (error) {
        setConnected(false);
      } else {
        setConnected(connected);
      }
    };
    validateConnection();
  }, [asset?.assetId, asset?.clientId, asset?.subClientId, reload]);

  if (!asset || !playbackStreaming.startDate) {
    return null;
  }

  return (
    <FloatingRightCard
      name={RightCard.playback}
      initialScale={1}
      rows={2}
      safeBottomPadding={284}
      defaultWidth={330}
    >
      <FlexColumn
        gap={theme.sizing.scale300}
        classNames={css({ height: '100%' })}
      >
        <FlexRow classNames={classes.centeredBetween}>
          <LabelLarge
            margin={0}
            style={{ fontWeight: theme.typography.HeadingXSmall.fontWeight }}
          >
            Reproducción - {formatDateFromTs(playbackStreaming.startDate, 'hh:mm:ss a')}
          </LabelLarge>
          <StyledTooltip
            content="Cerrar"
            showArrow={false}
          >
            <Button
              shape="circle"
              kind="tertiary"
              size="mini"
              onClick={() => {
                resetPlaybackStreaming();
              }}
            >
              <MaterialIcon
                name="close"
                size="16px"
              />
            </Button>
          </StyledTooltip>
        </FlexRow>
        {connected === undefined && (
          <Centered>
            <LoadingSpinner $size="large" />
          </Centered>
        )}
        {connected && <PlaybackWindow duration={duration} />}
        {connected === false && (
          <EmptyState
            description="Dispositivo desconectado."
            action={
              <Button
                shape="default"
                kind="secondary"
                size="mini"
                onClick={() => setReload((prev) => prev + 1)}
                startEnhancer={
                  <MaterialIcon
                    name="refresh"
                    size={theme.sizing.scale600}
                  />
                }
              >
                Recargar
              </Button>
            }
          />
        )}
      </FlexColumn>
    </FloatingRightCard>
  );
}

export default FloatingPlaybackWindow;
